package net.bothosting.docs.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

private data class TocEntry(val id: String, val text: String, val level: Int)

@Composable
fun TableOfContents() {
    val context = rememberPageContext()
    val routePath = context.route.path

    var headings by remember { mutableStateOf<List<TocEntry>>(emptyList()) }
    var activeId by remember { mutableStateOf("") }

    LaunchedEffect(routePath) {
        headings = emptyList()
        activeId = ""
        kotlinx.browser.window.setTimeout({
            val doc = kotlinx.browser.document
            val elements = doc.querySelectorAll(".content-area h1, .content-area h2, .content-area h3, .content-area h4")
            val items = mutableListOf<TocEntry>()
            for (i in 0 until elements.length) {
                val el = elements.item(i) as? org.w3c.dom.HTMLElement ?: continue
                if (el.id.isNotEmpty()) {
                    items.add(
                        TocEntry(
                            id = el.id,
                            text = el.textContent?.trim() ?: "",
                            level = el.tagName.removePrefix("h").toIntOrNull() ?: 1
                        )
                    )
                }
            }
            headings = items
            if (items.isNotEmpty() && activeId.isEmpty()) {
                activeId = items.first().id
            }
        }, 150)
    }

    DisposableEffect(routePath) {
        var observer: dynamic = null

        kotlinx.browser.window.setTimeout({
            val doc = kotlinx.browser.document
            val contentArea = doc.querySelector(".content-area") ?: return@setTimeout

            val intersecting = mutableMapOf<String, Boolean>()

            observer = js("""
                new IntersectionObserver(function(entries) {
                    for (var i = 0; i < entries.length; i++) {
                        var entry = entries[i];
                        window.__tocIntersecting[entry.target.id] = entry.isIntersecting;
                    }
                    var firstVisible = null;
                    var firstTop = Infinity;
                    var keys = Object.keys(window.__tocIntersecting);
                    for (var j = 0; j < keys.length; j++) {
                        var key = keys[j];
                        if (window.__tocIntersecting[key]) {
                            var el = document.getElementById(key);
                            if (el) {
                                var top = el.getBoundingClientRect().top;
                                if (top < firstTop) {
                                    firstTop = top;
                                    firstVisible = key;
                                }
                            }
                        }
                    }
                    if (firstVisible) {
                        window.__tocActiveId = firstVisible;
                    }
                }, { root: contentArea, rootMargin: '-70px 0px -60% 0px', threshold: 0 })
            """)

            js("window.__tocIntersecting = {}")
            js("window.__tocActiveId = ''")

            val allHeadings = contentArea.querySelectorAll("h1, h2, h3, h4")
            for (i in 0 until allHeadings.length) {
                val el = allHeadings.item(i)
                if (el != null) {
                    observer.observe(el)
                }
            }
        }, 200)

        val pollJob = kotlinx.browser.window.setInterval({
            val doc = kotlinx.browser.document
            val currentActive: String = js("window.__tocActiveId || ''")
            if (currentActive.isNotEmpty() && currentActive != activeId) {
                val el = doc.getElementById(currentActive)
                if (el != null) {
                    activeId = currentActive
                }
            }
        }, 100)

        onDispose {
            js("clearInterval")(pollJob)
            observer?.disconnect()
        }
    }

    if (headings.isEmpty()) return

    Column(
        Modifier
            .width(220.px)
            .flexShrink(0)
            .position(Position.Sticky)
            .top(0.px)
            .height(100.vh)
            .overflow { y(Overflow.Auto) }
            .styleModifier { property("border-left", "1px solid rgba(26,31,46,0.1)") }
            .padding(top = 2.cssRem, bottom = 3.cssRem, left = 1.5.cssRem, right = 1.cssRem)
    ) {
        Row(
            Modifier.gap(0.5.cssRem).margin(bottom = 0.75.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(
                Modifier.fontSize(0.7.cssRem).color(Color.rgb(0x5a6273))
                    .toAttrs { attr("class", "fas fa-list-ul") }
            )
            SpanText(
                "ON THIS PAGE",
                Modifier
                    .fontSize(0.7.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .letterSpacing(0.05.cssRem)
                    .color(Color.rgb(0x5a6273))
            )
        }

        headings.forEach { entry ->
            val isActive = entry.id == activeId
            val indent = (entry.level - 1) * 0.4

            Box(
                Modifier
                    .display(DisplayStyle.Block)
                    .padding(left = indent.cssRem)
                    .onClick {
                        val doc = kotlinx.browser.document
                        val el = doc.getElementById(entry.id)
                        el?.scrollIntoView(js("({ behavior: 'smooth', block: 'start' })"))
                        kotlinx.browser.window.history.replaceState(null, "", "#${entry.id}")
                        activeId = entry.id
                    }
                    .styleModifier { property("cursor", "pointer") }
            ) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(top = 0.3.cssRem, bottom = 0.3.cssRem, left = 0.65.cssRem)
                        .styleModifier {
                            property("border-left", if (isActive) "2px solid #3b9eff" else "2px solid transparent")
                            property("transition", "border-color 0.2s ease, color 0.15s ease")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        entry.text,
                        Modifier
                            .fontSize(0.8.cssRem)
                            .lineHeight(1.4)
                            .then(
                                if (isActive) Modifier.color(Color.rgb(0x3b9eff))
                                else Modifier.color(Color.rgb(0x828a9b))
                            )
                    )
                }
            }
        }
    }
}
